import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  getOrderInitiationConfig,
  getQuoteData,
  getOrderInitiationScripInfo,
  orderInitiate,
  getStrategies,
} from "../../api/orderInitiation";
import {
  displayExpiryToApi,
  getApiFieldForScriptSelection,
  isScripDimensionApiField,
  formatScripValue,
  formatPctOfMarginRequired,
  parsePriceToNumber,
  parseCircuitLimitBound,
  clampNumericPriceToCircuit,
  scripInfoInputQuantity,
  orderQuantityFromQtyLotsField,
  mapOrderInitiationScripInfoResponse,
  EMPTY_SCRIPT_SELECTION,
  formatRewardRisk,
  formatBreakevenWithPct,
  applyScripDimensionCascade,
  isLimitOrderUiType,
  isQuoteSelectionComplete,
  optionTypeForApi,
  strikePriceForApi,
} from "./utils";
import { formatApiMessage } from "../../utils/formatApiMessage";
import { orderStatusNavigationStateFromResult, openOrderStatusInNewTab } from "../../utils/normalizeOrderStatusResponse";
import { useDebouncedIncrement } from "../../hooks/useDebouncedIncrement";
import { useOrderInitiateScriptKeys } from "../../hooks/useOrderInitiateScriptKeys";
import {
  buildOrderInitiationConfigRequestList,
  mergeSelectionsWithDropdownDefaults,
} from "./selectionUtils";
import {
  loadRecentScrips,
  rememberRecentScrip,
  recentScripEntryFromRow,
} from "./recentScripsStorage";
import { VALIDITY_OPTIONS } from "./constants";
import TopMetricsRow from "./TopMetricsRow";
import ScriptListSection from "./ScriptListSection";
import CombinedSelectionSection from "./CombinedSelectionSection";
import StickyFooter from "./StickyFooter";
import { useBasketsAndClients, useClientRows } from "./hooks/useOrderInitiateData";
import styles from "./OrderInitiatePage.module.css";

function cloneQuoteDataSnapshot(q) {
  if (!q || typeof q !== "object") return null;
  try {
    return JSON.parse(JSON.stringify(q));
  } catch {
    return { ...q };
  }
}

function circuitClampedOrderPrice(sel, q) {
  const raw =
    sel.price && String(sel.price).trim() !== ""
      ? parsePriceToNumber(sel.price)
      : q?.last_price != null
        ? Number(q.last_price)
        : 0;
  return clampNumericPriceToCircuit(
    raw,
    sel.orderType,
    parseCircuitLimitBound(q?.lower_circuit_limit),
    parseCircuitLimitBound(q?.upper_circuit_limit),
    sel.exchange
  );
}

function OrderInitiatePage() {
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [scriptIds, setScriptIds] = useState([1]);
  const [selectedScriptIds, setSelectedScriptIds] = useState(() => new Set([1]));
  const [lastAddedScriptId, setLastAddedScriptId] = useState(null);
  const [scriptSelections, setScriptSelections] = useState(() => [{ ...EMPTY_SCRIPT_SELECTION }]);
  const [scriptDropdowns, setScriptDropdowns] = useState(null);
  const [quoteDataList, setQuoteDataList] = useState([]);
  const [scripInfo, setScripInfo] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const lastAddedScriptRef = useRef(null);
  const clearAddedRef = useRef(null);
  const abortControllerRef = useRef(null);
  const quoteDataAbortRef = useRef(null);
  const scripInfoAbortRef = useRef(null);
  const orderInitiationConfigExchangeOnlyIndexesRef = useRef(null);
  const [dropdownConfigNonce, setDropdownConfigNonce] = useState(0);
  const [recentScrips, setRecentScrips] = useState(() => loadRecentScrips());
  const scriptSelectionsRef = useRef(scriptSelections);
  scriptSelectionsRef.current = scriptSelections;

  useEffect(() => {
    getStrategies()
      .then((data) => {
        if (Array.isArray(data)) {
          setStrategies(data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch strategies", err);
      });
  }, []);

  const strategyOptions = useMemo(
    () => strategies.map((s) => s.strategy_name),
    [strategies]
  );

  useEffect(() => {
    if (strategyOptions.length > 0) {
      setScriptSelections((prev) =>
        prev.map((sel) =>
          sel.strategy ? sel : { ...sel, strategy: strategyOptions[0] }
        )
      );
    }
  }, [strategyOptions]);

  const {
    baskets,
    selectedBasketIndices,
    hasBasketSelected,
    toggleBasket,
    filteredClients,
    clientsLoading,
    basketsLoading,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId,
    updateClientRow,
    handleClientHeaderCheckboxChange,
    applyMarginToDeployToAllClients,
    applyMultiplierToAllClients,
    persistLastSelectedClients,
    applyLastSelectedClients,
    lastSelectedChecked,
    lastSelectedDisabled,
  } = useBasketsAndClients();

  const marginRequiredFromMeta = useMemo(() => {
    const v = scripInfo?.MarginInfo?.InitialMarginRequired;
    if (v == null || v === "") return 0;
    const n = Number(String(v).replace(/,/g, "").trim());
    return Number.isNaN(n) ? 0 : n;
  }, [scripInfo]);

  const clientRows = useClientRows(
    filteredClients,
    marginRequiredFromMeta,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId
  );

  const allClientsChecked = clientRows.length > 0 && clientRows.every((r) => r.checked);
  const someClientsChecked = clientRows.some((r) => r.checked);

  const [clientSelectionPhase, setClientSelectionPhase] = useState("browse");
  const handleClientSelectionNext = useCallback(() => {
    persistLastSelectedClients();
    setClientSelectionPhase("selectedReview");
  }, [persistLastSelectedClients]);
  const handleClientSelectionBack = useCallback(() => setClientSelectionPhase("browse"), []);
  const handleToggleLastSelected = useCallback(
    (checked) => applyLastSelectedClients(checked),
    [applyLastSelectedClients]
  );

  const {
    scriptSelectionsKey,
    orderInputKey,
  } = useOrderInitiateScriptKeys(scriptSelections);

  const debouncedApiTrigger = useDebouncedIncrement(scriptSelectionsKey, 650);
  /** Quote API must follow selection changes — separate debounce from config (650ms) so payload is not stale. */
  const debouncedQuoteTrigger = useDebouncedIncrement(scriptSelectionsKey, 300);
  const debouncedOrderInputTrigger = useDebouncedIncrement(orderInputKey, 500);

  useEffect(() => {
    const exchangeOnlyIndexes = orderInitiationConfigExchangeOnlyIndexesRef.current;
    orderInitiationConfigExchangeOnlyIndexesRef.current = null;
    const requestList = buildOrderInitiationConfigRequestList(
      scriptSelectionsRef.current,
      exchangeOnlyIndexes
    );

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    getOrderInitiationConfig(requestList, { signal: abortControllerRef.current.signal })
      .then((data) => {
        setScriptDropdowns(data);
        const optsList = data?.quote_drop_down_options_list || [];
        if (optsList.length > 0) {
          setScriptSelections((prev) => {
            const next = mergeSelectionsWithDropdownDefaults(prev, optsList, {
              onlyIndexes: exchangeOnlyIndexes,
            });
            scriptSelectionsRef.current = next;
            return next;
          });
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Failed to load dropdown options", err);
      });
    return () => abortControllerRef.current?.abort();
  }, [debouncedApiTrigger, dropdownConfigNonce]);

  useEffect(() => {
    let isSubscribed = true;
    let timeoutId;
    const fetchQuotes = async () => {
      if (!isSubscribed) return;
      // Wait for async get_quote_drop_down_options — without lists we cannot align payload to API options.
      if (!scriptDropdowns?.quote_drop_down_options_list?.length) {
        setQuoteDataList([]);
        return;
      }
      const rows = scriptSelectionsRef.current || [];
      const completeIndexes = [];
      const requestList = [];
      rows.forEach((sel, i) => {
        if (!isQuoteSelectionComplete(sel)) return;
        completeIndexes.push(i);
        requestList.push({
          exchange: String(sel.exchange ?? "").trim(),
          segment: String(sel.segment ?? "").trim(),
          symbol: String(sel.symbol ?? "").trim(),
          option_type: optionTypeForApi(sel),
          strike_price: strikePriceForApi(sel),
          expiry_date: String(sel.expiry_date ?? "").trim(),
        });
      });
      if (requestList.length === 0) {
        setQuoteDataList((prev) => {
          if (!prev?.length) return prev;
          return rows.map((_, i) => prev[i] ?? null);
        });
        return;
      }
      if (quoteDataAbortRef.current) quoteDataAbortRef.current.abort();
      quoteDataAbortRef.current = new AbortController();
      try {
        const data = await getQuoteData(requestList, { signal: quoteDataAbortRef.current.signal });
        if (!isSubscribed) return;
        const quoteList = data?.quote_data_list ?? [];
        setQuoteDataList((prev) =>
          rows.map((_, i) => {
            const reqIdx = completeIndexes.indexOf(i);
            if (reqIdx < 0) return prev?.[i] ?? null;
            const item = quoteList[reqIdx] ?? null;
            if (!item) return null;
            return item.exchange != null
              ? item
              : { ...item, exchange: requestList[reqIdx]?.exchange ?? item.exchange };
          })
        );
      } catch (err) {
        if (err.name !== "AbortError") console.error("Failed to load quote data", err);
      }
      if (isSubscribed) timeoutId = setTimeout(fetchQuotes, 5000);
    };
    fetchQuotes();
    return () => {
      isSubscribed = false;
      if (timeoutId) clearTimeout(timeoutId);
      quoteDataAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuoteTrigger, scriptDropdowns]);

  useEffect(() => {
    const rows = scriptSelectionsRef.current || [];
    const paired = (quoteDataList || [])
      .map((q, i) => ({ q, sel: rows[i] || {}, i }))
      .filter(({ q }) => q && q.tradingsymbol && q.instrument_token);
    if (paired.length === 0) {
      setScripInfo(null);
      return;
    }
    if (scripInfoAbortRef.current) scripInfoAbortRef.current.abort();
    scripInfoAbortRef.current = new AbortController();
    const requestScripList = paired.map(({ q, sel }) => {
      return {
        trading_symbol: q.tradingsymbol,
        zerodha_instrument_token: String(q.instrument_token),
        order_price: circuitClampedOrderPrice(sel, q),
        transaction_type: sel.transactionType ? sel.transactionType.toUpperCase() : "BUY",
        input_quantity: scripInfoInputQuantity(sel.qtyLots, q.lot_size),
        lot_size: q.lot_size != null ? String(q.lot_size) : "1",
        exchange: sel.exchange || "NFO",
        product_type: sel.productType ? sel.productType.toUpperCase() : "DELIVERY",
      };
    });
    const underlyingToken = paired[0].q.underlying_instrument_token || 0;
    getOrderInitiationScripInfo(requestScripList, underlyingToken, {
      signal: scripInfoAbortRef.current.signal,
    })
      .then((data) => {
        const mapped = mapOrderInitiationScripInfoResponse(data);
        if (mapped) setScripInfo(mapped);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Failed to fetch scrip info", err);
      });
    return () => {
      scripInfoAbortRef.current?.abort();
    };
  }, [quoteDataList, debouncedOrderInputTrigger]);

  useEffect(() => {
    const rows = scriptSelectionsRef.current || [];
    (quoteDataList || []).forEach((q, i) => {
      const name = String(q?.tradingsymbol ?? "").trim();
      if (!name) return;
      const sel = rows[i];
      if (!sel || !isQuoteSelectionComplete(sel)) return;
      const entry = recentScripEntryFromRow(sel, name);
      if (!entry) return;
      setRecentScrips((prev) => rememberRecentScrip(entry, prev));
    });
  }, [quoteDataList, scriptSelections]);

  const handleAddScrip = useCallback(() => {
    if (scriptIds.length >= 10) return;
    const rows = scriptSelectionsRef.current;
    const last = rows[rows.length - 1];
    if (!last) return;
    const newSelection = { ...last };
    const newId = Date.now();
    setScriptIds((prev) => [...prev, newId]);
    setSelectedScriptIds((prev) => new Set([...prev, newId]));
    setScriptSelections((prev) => [...prev, newSelection]);
    setQuoteDataList((prev) => {
      const optimistic = cloneQuoteDataSnapshot(prev[prev.length - 1]);
      return [...prev, optimistic];
    });
    setLastAddedScriptId(newId);
  }, [scriptIds.length]);

  const handleAddRecentScrip = useCallback((entry) => {
    if (!entry?.name) return;
    const rows = scriptSelectionsRef.current || [];
    const last = rows[rows.length - 1];
    const newSelection = {
      ...EMPTY_SCRIPT_SELECTION,
      transactionType: entry.transactionType === "Sell" ? "Sell" : "Buy",
      qtyLots: last?.qtyLots || "1",
      productType: last?.productType || "",
      orderType: last?.orderType || "",
      validity: last?.validity || "",
      strategy: last?.strategy || "",
      exchange: entry.exchange || "",
      segment: entry.segment || "",
      symbol: entry.symbol || "",
      option_type: entry.option_type || "",
      strike_price: entry.strike_price ?? "",
      expiry_date: entry.expiry_date || "",
    };

    const onlyIncomplete =
      rows.length === 1 && last && !isQuoteSelectionComplete(last);

    if (onlyIncomplete) {
      scriptSelectionsRef.current = [newSelection];
      setScriptSelections([newSelection]);
      setQuoteDataList([null]);
      setDropdownConfigNonce((n) => n + 1);
      return;
    }

    if (scriptIds.length >= 10) return;
    const newId = Date.now();
    setScriptIds((prev) => [...prev, newId]);
    setSelectedScriptIds((prev) => new Set([...prev, newId]));
    setScriptSelections((prev) => {
      const next = [...prev, newSelection];
      scriptSelectionsRef.current = next;
      return next;
    });
    setQuoteDataList((prev) => [...prev, null]);
    setLastAddedScriptId(newId);
    setDropdownConfigNonce((n) => n + 1);
  }, [scriptIds.length]);

  useEffect(() => {
    if (lastAddedScriptId == null) return;
    const scrollTimer = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        lastAddedScriptRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    });
    clearAddedRef.current = setTimeout(() => setLastAddedScriptId(null), 500);
    return () => {
      cancelAnimationFrame(scrollTimer);
      if (clearAddedRef.current) clearTimeout(clearAddedRef.current);
    };
  }, [lastAddedScriptId]);

  const handleDeleteScrip = useCallback((id, index) => {
    setScriptIds((prev) => (prev.length <= 1 ? prev : prev.filter((sid) => sid !== id)));
    setSelectedScriptIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setScriptSelections((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    setQuoteDataList((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }, []);

  const handleScriptSelectedChange = useCallback((scriptId, checked) => {
    setSelectedScriptIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(scriptId);
      else next.delete(scriptId);
      return next;
    });
  }, []);

  const handleScriptSelectionChange = useCallback((scriptIndex, field, value) => {
    const apiField = getApiFieldForScriptSelection(field);
    const apiValue = apiField === "expiry_date" ? displayExpiryToApi(value) : value;

    if (apiField === "exchange") {
      const prevRow = scriptSelectionsRef.current[scriptIndex];
      const prevEx = (prevRow?.exchange ?? "").toString().trim();
      const nextEx = (apiValue ?? "").toString().trim();
      if (prevEx !== nextEx) {
        orderInitiationConfigExchangeOnlyIndexesRef.current = new Set([scriptIndex]);
      }
    } else if (isScripDimensionApiField(apiField)) {
      orderInitiationConfigExchangeOnlyIndexesRef.current = null;
    }

    setScriptSelections((prev) => {
      const next = prev.map((s, i) => {
        if (i !== scriptIndex) return s;
        return applyScripDimensionCascade(s, apiField, apiValue);
      });
      // Keep ref in sync immediately so in-flight quote polls never read pre-cascade values
      scriptSelectionsRef.current = next;
      return next;
    });

    if (apiField === "exchange" || apiField === "segment" || apiField === "symbol") {
      setDropdownConfigNonce((n) => n + 1);
    }

    // Drop quotes as soon as a parent dropdown clears dependents
    if (
      apiField === "exchange" ||
      apiField === "segment" ||
      apiField === "symbol"
    ) {
      const row = scriptSelectionsRef.current[scriptIndex];
      if (!isQuoteSelectionComplete(row)) {
        setQuoteDataList((prev) => {
          if (!prev?.length) return prev;
          const copy = [...prev];
          copy[scriptIndex] = null;
          return copy;
        });
        setScripInfo(null);
      }
    }
  }, []);

  const orderTypeToApi = useCallback((orderType) => {
    if (!orderType) return "BID_ASK";
    const t = String(orderType).trim().toUpperCase().replace(/[-\s]+/g, "_");
    // UI: "Bid/Ask" → BID_ASK (same as Order Book / QuoteBox)
    if (t.includes("BID") && t.includes("ASK")) return "BID_ASK";
    if (t === "BID_ASK" || t === "BIDASK") return "BID_ASK";
    if (t.includes("LIMIT")) return "LIMIT";
    if (t.includes("EXPIRY")) return "EXPIRY";
    if (t.includes("MARKET")) return "MARKET";
    if (t === "AMO") return "AMO";
    return "BID_ASK";
  }, []);

  const handlePlaceOrder = useCallback(async () => {
    const selectedScripCount = selectedScriptIds.size;
    const totalClientsCount = clientRows.filter((r) => r.checked).length;
    const clientGateOk = clientSelectionPhase === "selectedReview";
    const selectedIndicesForValidation = scriptIds
      .map((id, index) => (selectedScriptIds.has(id) ? index : null))
      .filter((i) => i !== null);

    const pricesOk = selectedIndicesForValidation.every((index) => {
      const sel = scriptSelections[index] || {};
      return String(sel.price ?? "").trim() !== "";
    });
    const canPlace =
      selectedScripCount > 0 && clientGateOk && totalClientsCount > 0 && pricesOk;
    if (!canPlace) return;

    const selectedIndices = scriptIds
      .map((id, index) => (selectedScriptIds.has(id) ? index : null))
      .filter((i) => i !== null);
    const scripList = [];
    for (const index of selectedIndices) {
      const q = quoteDataList[index];
      const sel = scriptSelections[index] || {};
      const tradingSymbol = (q?.tradingsymbol) || sel.symbol || "";
      if (!tradingSymbol || !q?.instrument_token) continue;
      const lotSize = q.lot_size != null ? Number(q.lot_size) : 1;
      const orderQty = orderQuantityFromQtyLotsField(sel.qtyLots, lotSize);
      const disclosedQty =
        sel.absDisQty && String(sel.absDisQty).trim() !== ""
          ? Number(sel.absDisQty)
          : orderQty;
      const orderPrice = circuitClampedOrderPrice(sel, q);
      const strategyFromScrip = (sel.strategy && String(sel.strategy).trim()) || strategyOptions[0] || "";
      scripList.push({
        trading_symbol: tradingSymbol,
        zerodha_instrument_token: Number(q.instrument_token),
        order_quantity: orderQty,
        disclosed_quantity: disclosedQty,
        transaction_type: (sel.transactionType || "Buy").toUpperCase(),
        product_type: (sel.productType || "DELIVERY").toUpperCase(),
        order_type: orderTypeToApi(sel.orderType),
        order_price: orderPrice,
        trigger_price: 0,
        lot_size: lotSize,
        exchange: (sel.exchange || "NSE").toUpperCase(),
        strategy_name: strategyFromScrip || "",
        validity: (sel.validity || "DAY").toUpperCase(),
      });
    }
    const client_multipliers = {};
    clientRows
      .filter((r) => r.checked)
      .forEach((row) => {
        const multStr =
          row.multiplier && String(row.multiplier).trim() !== ""
            ? row.multiplier
            : row.suggestedMultiplier;
        const mult = parseInt(String(multStr).trim(), 10);
        client_multipliers[row.clientCode] = Number.isNaN(mult) ? 0 : mult;
      });
    if (scripList.length === 0 || Object.keys(client_multipliers).length === 0) return;
    setIsPlacingOrder(true);
    try {
      const result = await orderInitiate(scripList, client_multipliers);
      openOrderStatusInNewTab(orderStatusNavigationStateFromResult(result));
    } catch (err) {
      console.error("Place order failed", err);
      const msg =
        formatApiMessage(err?.response?.data?.detail) ??
        formatApiMessage(err?.response?.data?.message) ??
        (typeof err?.message === "string" && err.message.trim() !== "" ? err.message : null) ??
        "Place order failed.";
      openOrderStatusInNewTab({ squareOffError: msg });
    } finally {
      setIsPlacingOrder(false);
    }
  }, [
    selectedScriptIds,
    clientRows,
    scriptIds,
    quoteDataList,
    scriptSelections,
    strategyOptions,
    orderTypeToApi,
    clientSelectionPhase,
  ]);

  const selectedScripCount = selectedScriptIds.size;
  const selectedBasketCount = selectedBasketIndices.size;
  const totalClientsCount = clientRows.filter((r) => r.checked).length;
  const selectedIndicesForValidation = scriptIds
    .map((id, index) => (selectedScriptIds.has(id) ? index : null))
    .filter((i) => i !== null);

  const pricesOk = selectedIndicesForValidation.every((index) => {
    const sel = scriptSelections[index] || {};
    return String(sel.price ?? "").trim() !== "";
  });

  const canGoNext = someClientsChecked;
  const canPlaceOrder =
    clientSelectionPhase === "selectedReview" &&
    selectedScripCount > 0 &&
    totalClientsCount > 0 &&
    pricesOk;
  
  const { maxProfitVal, minProfitVal, maxProfitPct, maxLossPct, metricsGrid } = useMemo(() => {
    const marginForPct = scripInfo?.MarginInfo?.InitialMarginRequired;
    const maxProfitValInner = scripInfo ? formatScripValue(scripInfo.StrategyInfo?.MaxProfit) : "—";
    const minProfitValInner = scripInfo ? formatScripValue(scripInfo.StrategyInfo?.MaxLoss) : "—";
    const maxProfitPctInner = scripInfo
      ? formatPctOfMarginRequired(scripInfo.StrategyInfo?.MaxProfit, marginForPct)
      : "—";
    const maxLossPctInner = scripInfo
      ? formatPctOfMarginRequired(scripInfo.StrategyInfo?.MaxLoss, marginForPct)
      : "—";
    const breakevenList = Array.isArray(scripInfo?.StrategyInfo?.BreakevenList)
      ? scripInfo.StrategyInfo.BreakevenList.filter((v) => v != null && v !== "")
      : [];
    const breakevenValue = scripInfo
      ? formatBreakevenWithPct(breakevenList, scripInfo.StrategyInfo?.UnderlyingPrice)
      : "—";

    const grid = [
      {
        value: breakevenValue,
        label: "Breakeven",
        growToFit: true,
      },
      {
        value: scripInfo ? formatScripValue(scripInfo.StrategyInfo?.PremiumPerLot) : "—",
        label: "Premium/lot",
      },
      {
        value: scripInfo ? formatScripValue(scripInfo.StrategyInfo?.TotalPremium) : "—",
        label: "Total Premium",
      },
      {
        value: scripInfo ? formatScripValue(scripInfo.MarginInfo?.InitialMarginRequired) : "—",
        label: "Margin Required",
      },
      {
        value: scripInfo
          ? formatRewardRisk(scripInfo.StrategyInfo?.MaxProfit, scripInfo.StrategyInfo?.MaxLoss)
          : "—",
        label: "Reward/Risk",
      },
    ];
    return {
      maxProfitVal: maxProfitValInner,
      minProfitVal: minProfitValInner,
      maxProfitPct: maxProfitPctInner,
      maxLossPct: maxLossPctInner,
      metricsGrid: grid,
    };
  }, [scripInfo]);

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <TopMetricsRow
          maxProfitVal={maxProfitVal}
          minProfitVal={minProfitVal}
          maxProfitPct={maxProfitPct}
          maxLossPct={maxLossPct}
          metricsGrid={metricsGrid}
        />
      </div>

      <ScriptListSection
        scriptIds={scriptIds}
        lastAddedScriptId={lastAddedScriptId}
        lastAddedScriptRef={lastAddedScriptRef}
        scriptDropdowns={scriptDropdowns}
        scriptSelections={scriptSelections}
        quoteDataList={quoteDataList}
        selectedScriptIds={selectedScriptIds}
        validityOptions={VALIDITY_OPTIONS}
        onAddScrip={handleAddScrip}
        onAddRecentScrip={handleAddRecentScrip}
        recentScrips={recentScrips}
        onDeleteScrip={handleDeleteScrip}
        onScriptSelectionChange={handleScriptSelectionChange}
        onScriptSelectedChange={handleScriptSelectedChange}
      />

      <CombinedSelectionSection
        baskets={baskets}
        selectedBasketIndices={selectedBasketIndices}
        toggleBasket={toggleBasket}
        hasBasketSelected={hasBasketSelected}
        basketClientsLoading={clientsLoading}
        basketsLoading={basketsLoading}
        clientRows={clientRows}
        updateClientRow={updateClientRow}
        applyMarginToDeployToAllClients={applyMarginToDeployToAllClients}
        applyMultiplierToAllClients={applyMultiplierToAllClients}
        allClientsChecked={allClientsChecked}
        someClientsChecked={someClientsChecked}
        onClientHeaderCheckboxChange={handleClientHeaderCheckboxChange}
        clientSelectionPhase={clientSelectionPhase}
        lastSelectedChecked={lastSelectedChecked}
        lastSelectedDisabled={lastSelectedDisabled}
        onToggleLastSelected={handleToggleLastSelected}
      />

      <StickyFooter
        selectedScripCount={selectedScripCount}
        selectedBasketCount={selectedBasketCount}
        totalClientsCount={totalClientsCount}
        clientSelectionPhase={clientSelectionPhase}
        canGoNext={canGoNext}
        canPlaceOrder={canPlaceOrder}
        isPlacingOrder={isPlacingOrder}
        onNext={handleClientSelectionNext}
        onBack={handleClientSelectionBack}
        onPlaceOrder={handlePlaceOrder}
      />
    </div>
  );
}

export default OrderInitiatePage;
